import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BanknotesIcon,
  BellAlertIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  formatCurrency,
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePersonCell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { getVehicleIdFromSearch } from './vehicleQueryParams';

const initialForm = {
  vehicle: '',
  fine_date: '',
  notification_date: '',
  amount: '',
  early_payment_date: '',
  user_notified: false,
  collected: false,
  notes: '',
};

const BooleanBadge = ({ value }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
      value
        ? 'bg-emerald-100 text-emerald-800 ring-emerald-200/80 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800/50'
        : 'bg-amber-100 text-amber-900 ring-amber-200/80 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/50'
    }`}
  >
    {value ? 'Evet' : 'Hayır'}
  </span>
);

const InternalAuditTrafficFinesPage = () => {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState({
    vehicle_id: getVehicleIdFromSearch(),
    user_notified: '',
    collected: '',
  });
  const [draftFilters, setDraftFilters] = useState({
    vehicle_id: getVehicleIdFromSearch(),
    user_notified: '',
    collected: '',
  });
  const vehicleDropdownRef = useRef(null);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);
  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [finesResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getTrafficFines(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      setRows(finesResponse?.data || []);
      setVehicles(vehiclesResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Trafik cezası kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '', user_notified: '', collected: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setFormData({
      vehicle: row.vehicle || '',
      fine_date: row.fine_date || '',
      notification_date: row.notification_date || '',
      amount: row.amount ?? '',
      early_payment_date: row.early_payment_date || '',
      user_notified: Boolean(row.user_notified),
      collected: Boolean(row.collected),
      notes: row.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('Trafik cezası kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteTrafficFine(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        vehicle: formData.vehicle ? Number(formData.vehicle) : null,
        fine_date: formData.fine_date || null,
        notification_date: formData.notification_date || null,
        early_payment_date: formData.early_payment_date || null,
        amount: formData.amount !== '' ? Number(formData.amount) : null,
        user_notified: Boolean(formData.user_notified),
        collected: Boolean(formData.collected),
        notes: formData.notes || null,
      };
      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateTrafficFine(editingRow.id, payload);
      } else {
        await vehicleMaintenanceApi.createTrafficFine(payload);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ getValue }) => (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {getValue() || '—'}
        </span>
      ),
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => {
        const vehicle = getVehicleById(vehicles, row.original.vehicle);
        return (
          <VehiclePlateCell
            plate={row.original.license_plate}
            brand={vehicle?.brand}
            model={vehicle?.model}
          />
        );
      },
    },
    {
      header: 'Zimmetli',
      accessorKey: 'assigned_person',
      cell: ({ row }) => <VehiclePersonCell name={row.original.assigned_person} />,
    },
    { header: 'Ceza Tarihi', accessorKey: 'fine_date', cell: ({ getValue }) => getValue() || '—' },
    { header: 'Tebliğ Tarihi', accessorKey: 'notification_date', cell: ({ getValue }) => getValue() || '—' },
    { header: 'Ceza Tutarı', accessorKey: 'amount', cell: ({ getValue }) => formatCurrency(getValue()) },
    { header: 'Erken Ödeme Tarihi', accessorKey: 'early_payment_date', cell: ({ getValue }) => getValue() || '—' },
    {
      header: 'Kullanıcıya Bildirildi',
      accessorKey: 'user_notified',
      cell: ({ getValue }) => <BooleanBadge value={Boolean(getValue())} />,
    },
    {
      header: 'Tahsil Edildi',
      accessorKey: 'collected',
      cell: ({ getValue }) => <BooleanBadge value={Boolean(getValue())} />,
    },
    { header: 'Notlar', accessorKey: 'notes', cell: ({ getValue }) => getValue() || '—' },
  ], [vehicles]);

  const stats = useMemo(() => {
    const total = sortedRows.length;
    const unpaid = sortedRows.filter((r) => !r.collected).length;
    const unnotified = sortedRows.filter((r) => !r.user_notified).length;
    const totalAmount = sortedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    return { total, unpaid, unnotified, totalAmount: formatCurrency(totalAmount) };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: selectedRow.assigned_person,
    };
  }, [selectedRow, vehicles]);

  const getRowClassName = (row) => {
    if (!row.collected) return 'border-l-[3px] border-l-rose-400 dark:border-l-rose-500';
    if (!row.user_notified) return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    return '';
  };

  const renderTriStateFilter = (label, field) => (
    <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
      <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">{label}</legend>
      <select
        className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white"
        value={draftFilters[field]}
        onChange={(e) => setDraftFilters((prev) => ({ ...prev, [field]: e.target.value }))}
      >
        <option value="">Tümü</option>
        <option value="true">Evet</option>
        <option value="false">Hayır</option>
      </select>
    </fieldset>
  );

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={BanknotesIcon}
          title="Trafik Cezaları"
          subtitle="Araç trafik cezalarını takip edin, bildirim ve tahsilat durumunu yönetin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Ceza" value={stats.total} tone="slate" icon={BanknotesIcon} />
          <VehicleStatCard label="Tahsil Edilmedi" value={stats.unpaid} tone="rose" icon={ExclamationTriangleIcon} />
          <VehicleStatCard label="Bildirilmedi" value={stats.unnotified} tone="amber" icon={BellAlertIcon} />
          <VehicleStatCard label="Toplam Tutar" value={stats.totalAmount} tone="blue" icon={BanknotesIcon} />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview
            vehicle={selectedVehiclePreview}
            personName={selectedRow?.assigned_person || selectedVehiclePreview.assigned_person}
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Filtreler
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
                {renderTriStateFilter('Bildirildi', 'user_notified')}
                {renderTriStateFilter('Tahsil Edildi', 'collected')}
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(selectedRow)} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(selectedRow)} title="Sil" disabled={!selectedRow} />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          getRowClassName={getRowClassName}
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-3xl">
        <div className="px-4 pt-5 pb-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Trafik Cezasını Düzenle' : 'Yeni Trafik Cezası'}
            </h2>
            <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select
                  required
                  className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.vehicle}
                  onChange={(e) => setFormData((prev) => ({ ...prev, vehicle: e.target.value }))}
                >
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Ceza Tutarı (₺)</Label>
                <Input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Ceza Tarihi</Label>
                <Input
                  required
                  type="date"
                  value={formData.fine_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fine_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Tebliğ Tarihi</Label>
                <Input
                  type="date"
                  value={formData.notification_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notification_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Erken Ödeme Tarihi</Label>
                <Input
                  type="date"
                  value={formData.early_payment_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, early_payment_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1 flex flex-col justify-end">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.user_notified}
                    onChange={(e) => setFormData((prev) => ({ ...prev, user_notified: e.target.checked }))}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Kullanıcıya bildirildi
                </label>
              </div>
              <div className="space-y-1 flex flex-col justify-end">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.collected}
                    onChange={(e) => setFormData((prev) => ({ ...prev, collected: e.target.checked }))}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Tahsil edildi
                </label>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Notlar</Label>
                <Input value={formData.notes} onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditTrafficFinesPage;
